import type { GrammarTopic } from './types';

export const modalsTopics: GrammarTopic[] = [
  {
    slug: 'comparatives-superlatives',
    title: 'Comparatives & Superlatives',
    blocks: [
      { kind: 'h3', text: '1. Comparative adjectives' },
      {
        kind: 'p',
        text: 'Comparative adjectives are used to compare differences between the two objects they modify (larger, smaller, faster, higher). They are used in sentences where two nouns are compared, in this pattern:',
      },
      {
        kind: 'examples',
        items: [
          'My English is **better than** it was last year.',
          'This class is **easier than** I expected.',
          'Speaking English with other women is **more comfortable than** speaking alone.',
          'Working from home is **more flexible than** working in an office.',
        ],
      },
      { kind: 'h3', text: '2. Superlative adjectives' },
      {
        kind: 'p',
        text: 'Superlative adjectives are used to describe an object which is at the upper or lower limit of quality (the tallest, the smallest, the fastest, the highest). They are used in sentences where a subject is compared to a group of objects.',
      },
      {
        kind: 'examples',
        items: [
          'That was **the best** English class I\'ve ever had.',
          'English was **the hardest** subject for me at school.',
          'This is **the most expensive** restaurant in the neighborhood.',
          'She is **the most confident** person in the team.',
        ],
      },
      { kind: 'h3', text: '3. Equative adjectives' },
      {
        kind: 'p',
        text: 'Equatives and non-equatives are used to show equality or inequality between nouns or pronouns.',
      },
      {
        kind: 'examples',
        items: [
          'Speaking English is **as important as** understanding grammar.',
          'My new job is **as challenging as** my old one.',
          'Speaking English in a group is **not as scary as** I thought.',
          'My English homework is **not as difficult as** it used to be.',
        ],
      },
      { kind: 'h3', text: 'How are the comparative and superlative adjectives formed?' },
      { kind: 'h3', text: 'a. Short adjectives' },
      {
        kind: 'p',
        text: 'Add -er for the comparative and -est for the superlative. If the adjective has a consonant + single vowel + consonant spelling, the final consonant must be doubled before adding the ending.',
      },
      {
        kind: 'table',
        header: ['Adjective', 'Comparative', 'Superlative'],
        rows: [
          ['tall', 'taller', 'tallest'],
          ['fat', 'fatter', 'fattest'],
          ['big', 'bigger', 'biggest'],
          ['sad', 'sadder', 'saddest'],
        ],
      },
      { kind: 'h3', text: 'b. Long adjectives' },
      {
        kind: 'p',
        text: 'Adjectives with three or more syllables form the comparative by putting more in front of the adjective and the superlative by putting most in front.',
      },
      {
        kind: 'table',
        header: ['Adjective', 'Comparative', 'Superlative'],
        rows: [
          ['important', 'more important than', 'The most important'],
          ['expensive', 'more expensive than', 'The most expensive'],
        ],
      },
      { kind: 'h3', text: 'c. Irregular comparatives and superlatives' },
      {
        kind: 'p',
        text: 'These very common adjectives have completely irregular comparative and superlative forms.',
      },
      {
        kind: 'table',
        header: ['Adjective', 'Comparative', 'Superlative'],
        rows: [
          ['good', 'better', 'best'],
          ['bad', 'worse', 'worst'],
          ['little', 'less', 'least'],
          ['much', 'more', 'most'],
          ['far', 'further / farther', 'furthest / farthest'],
        ],
      },
      {
        kind: 'examples',
        items: [
          'My English is **better than** it was last year.',
          'I had **the worst** day at work yesterday.',
          'This option is **less expensive than** the other one.',
          'This is **the least expensive** option.',
          'I need **more** time to finish the project.',
          'This was **the best** decision for me.',
        ],
      },
    ],
  },
  {
    slug: 'correlative-comparatives',
    title: 'Correlative Comparatives',
    blocks: [
      {
        kind: 'p',
        text: 'Correlative comparatives show that two things change together. As one thing changes, another thing changes too.',
      },
      {
        kind: 'p',
        text: 'Basic Structure: The + comparative..., the + comparative...',
      },
      {
        kind: 'examples',
        items: [
          '**The more** I practice, **the more confident** I feel.',
          '**The more** you speak English, **the easier** it becomes.',
          '**The longer** you wait, **the harder** it becomes to start.',
          '**The more** we learn, **the more** we understand.',
        ],
      },
      {
        kind: 'p',
        text: 'Correlative comparatives can be differentiated according to the outcome, that can be expected or unexpected.',
      },
      { kind: 'h3', text: 'A. When the outcome is expected' },
      {
        kind: 'p',
        text: 'Sometimes the two changes follow a relationship we expect.',
      },
      {
        kind: 'examples',
        items: [
          '**The more** you read, **the more** vocabulary you learn.',
          '**The more** you speak, **the easier** it becomes.',
          '**The more** experience you have, **the more** opportunities you may have.',
          '**The higher** your salary, **the more** financial freedom you may have.',
        ],
      },
      { kind: 'h3', text: 'B. When the outcome is unexpected or contrary' },
      {
        kind: 'p',
        text: 'Sometimes the second change is different from what we might expect.',
      },
      {
        kind: 'examples',
        items: [
          '**The more** we learn, **the less** we know.',
          '**The higher** you go, **the harder** you fall.',
          '**The less** I know, **the better**.',
          '**The smaller** the company, **the more** problems you’ll find.',
        ],
      },
    ],
  },
  {
    slug: 'making-polite-requests',
    title: 'Making Polite Requests',
    blocks: [
      {
        kind: 'p',
        text: 'In English, certain phrases and verbs are better and more polite to make requests.',
      },
      {
        kind: 'p',
        text: '1. We use “could you” and “would you” as polite ways of telling or asking someone to do something.',
      },
      {
        kind: 'examples',
        items: [
          '**Could you** take a message, please?',
          '**Would you** carry this for me, please?',
        ],
      },
      {
        kind: 'p',
        text: '2. We use “would you like (to)” for invitations:',
      },
      {
        kind: 'examples',
        items: [
          '**Would you like to** come round tomorrow?',
          '**Would you like** another drink?',
        ],
      },
      {
        kind: 'p',
        text: '3. We use “can I” or “may I” to make offers:',
      },
      {
        kind: 'examples',
        items: ['**Can I** help you?', '**Can I** do that for you?'],
      },
    ],
  },
  {
    slug: 'modal-verbs-overview',
    title: 'Modal Verbs: A General Overview',
    blocks: [
      {
        kind: 'p',
        text: 'In the English Language, modal verbs are special auxiliary verbs that are used to express possibility, ability, permission, requests, suggestions, and necessity.',
      },
      {
        kind: 'p',
        text: 'They are auxiliary verbs because we use them as auxiliaries to form negative and interrogative sentences. They are special because they carry meaning, while DO, DID, WILL, and HAVE don’t carry any meaning.',
      },
      { kind: 'h3', text: '1. Possibility' },
      {
        kind: 'p',
        text: 'In situations when something is possible but not certain, use the modal verbs could, can, may, or might.',
      },
      {
        kind: 'examples',
        items: [
          'Judging by the clouds, it **might/may/could/can rain** today.',
          'She **may not become** the youngest soccer player ever.',
        ],
      },
      { kind: 'h3', text: '2. Ability' },
      {
        kind: 'p',
        text: 'The modal verb “can” and “be able to” show if the subject is able to do something, such as perform an action or demonstrate an ability. The negative form, cannot or can’t, shows that the subject is unable to do something.',
      },
      {
        kind: 'examples',
        items: [
          'She **can speak** three languages: Portuguese, English, and Spanish.',
          'My mom **can cook** the best chocolate cake ever.',
          'I **can’t ride** a bike, I have never learned it! **Can you ride** a bike?',
        ],
      },
      { kind: 'h3', text: '3. Asking permission' },
      {
        kind: 'p',
        text: 'If you want to ask permission to do something, start your question with can, may, or could. In more formal and polite usage, may is better for permission.',
      },
      {
        kind: 'examples',
        items: [
          '**May I leave** early today?',
          '**Could I play** too?',
          '**Can I go** to the restroom? **Could we take** a break?',
        ],
      },
      { kind: 'h3', text: '4. Request/asking for favor' },
      {
        kind: 'p',
        text: 'If you want to ask someone else to do something, start your question with will, would, can, or could.',
      },
      {
        kind: 'examples',
        items: [
          '**Would you get** that box for me?',
          '**Could you turn** that music down?',
          '**Will you come** here for a minute, please?',
        ],
      },
      { kind: 'h3', text: '5. Suggestion' },
      {
        kind: 'p',
        text: 'What if you want to recommend something but not command it? If you’re giving suggestions or advice without ordering someone around, you can use the modal verb should.',
      },
      {
        kind: 'examples',
        items: [
          'You **should try** the lasa(g)na.',
          'That guy **should wear** less colo(g)ne/parfum.',
        ],
      },
      { kind: 'h3', text: '6. Necessity' },
      {
        kind: 'p',
        text: 'When you are talking about a strong necessity, something that needs to be done, you should use must or have to. In the negative, these two modal verbs are related to prohibition too.',
      },
      {
        kind: 'examples',
        items: [
          'You **must do** your homework every week.',
          'You **mustn’t drive** and text at the same time.',
          'You **have to listen** to your mother’s advice.',
        ],
      },
    ],
  },
  {
    slug: 'modals-of-ability',
    title: 'Modal Verbs of Ability: Can & Be Able To',
    blocks: [
      {
        kind: 'p',
        text: 'When talking about skills and abilities in English, there are some modal verbs that you should have in mind.',
      },
      { kind: 'h3', text: '1. CAN and CAN’T' },
      {
        kind: 'p',
        text: 'Used to talk about abilities and skills we possess in the present.',
      },
      {
        kind: 'examples',
        items: [
          '→ I **can’t play** the guitar.',
          '→ She **cannot solve** complex math problems.',
          '→ They **can speak** three languages fluently.',
          '→ We **can finish** the project by the deadline.',
          '→ **Can he run** a marathon in under three hours?',
        ],
      },
      { kind: 'h3', text: '2. BE ABLE TO and NOT BE ABLE TO' },
      {
        kind: 'p',
        text: 'Used to talk about general skills, but it can be conjugated in the present, past or future.',
      },
      {
        kind: 'examples',
        items: [
          '→ I **am not able to operate** heavy machinery safely.',
          '→ They **were able to find** a solution to the challenging puzzle.',
          '→ **She\'s able to manage** multiple projects simultaneously.',
          '→ After months of practice, he **wasn’t able to do** advanced yoga poses.',
          '→ We **will be able to attend** the conference next week.',
        ],
      },
    ],
  },
  {
    slug: 'modals-of-past-ability',
    title: 'Modal Verbs of Past Ability: Could & Couldn’t',
    blocks: [
      {
        kind: 'p',
        text: 'We can use could and couldn\'t to talk about abilities in the past. The modal verb could is the past of the modal verb can.',
      },
      {
        kind: 'examples',
        items: [
          'I **could swim** when I was four.',
          'She **couldn\'t ride** a bike when she was three.',
          'He **could walk** when he was two.',
        ],
      },
      {
        kind: 'p',
        text: '1. To make the affirmative, we use the modal verb could + verb in the base form.',
      },
      {
        kind: 'examples',
        items: ['We **could read** when we were five.'],
      },
      {
        kind: 'p',
        text: '2. To make the negative form, we use could not or the contraction couldn’t.',
      },
      {
        kind: 'examples',
        items: ['My mum **couldn\'t swim** when she was a child.'],
      },
      {
        kind: 'p',
        text: '3. To make questions, we invert the order of could and the subject.',
      },
      {
        kind: 'examples',
        items: [
          '**Could you play** chess last year? Yes, I **could**.',
          '**Could your dad drive** a car when he was young? No, he **couldn\'t**.',
          'What **could you do** when you were four?',
        ],
      },
    ],
  },
  {
    slug: 'modals-of-advice',
    title: 'Modals of Advice: Should, Ought To, Had Better',
    blocks: [
      {
        kind: 'p',
        text: '1. We use should and shouldn\'t to make suggestions and give advice:',
      },
      {
        kind: 'examples',
        items: [
          'You **should stop** following toxic influencers on Instagram.',
          'Young children **shouldn\'t watch** violent TV shows.',
          'I have a problem. **Should I call** my parents or my friend?',
        ],
      },
      {
        kind: 'p',
        text: '2. We use ought to to give advice. The negative “ought not to” is not anymore used.',
      },
      {
        kind: 'examples',
        items: ['You **ought to accept** yourself.'],
      },
      {
        kind: 'p',
        text: '3. We use had better and hadn’t better to express stronger suggestions. It includes the idea of a warning: something bad will happen if you do not follow my advice.',
      },
      {
        kind: 'examples',
        items: [
          '**You’d better start** believing in girls’ ability to be anything they want.',
        ],
      },
      {
        kind: 'p',
        text: '4. I would suggest, and I would recommend are informal ways of suggesting something.',
      },
      {
        kind: 'examples',
        items: [
          '**I would recommend** reflecting on why a girl’s aspirations are usually lower than a boy’s (aspirations).',
        ],
      },
      {
        kind: 'p',
        text: '5. Why don’t you and How about are also informal ways of suggesting something to someone.',
      },
      {
        kind: 'examples',
        items: [
          '**Why don’t we watch** Barbie’s movie tonight?',
          '**How about watching** Barbie’s movie tonight?',
        ],
      },
    ],
  },
  {
    slug: 'modals-of-necessity',
    title: 'Modals of Necessity: Must and Have To',
    blocks: [
      {
        kind: 'p',
        text: '1. We use must and mustn\'t to say that it is necessary (or not) to do something. Must is the strongest and most serious modal verb of necessity.',
      },
      {
        kind: 'examples',
        items: [
          'You **must spend** your precious time learning something new.',
          'You **mustn’t diminish** girls’ ambitions when they are young.',
        ],
      },
      {
        kind: 'p',
        text: '2. We use have to and don’t have to to say that it is necessary (or not) to do something:',
      },
      {
        kind: 'examples',
        items: [
          'You **have to understand** that the media creates beauty standards, but you **don’t have to feel** bad because you are not a part of it.',
        ],
      },
      {
        kind: 'p',
        text: '3. We use have got to (you’ve gotta) in informal speech.',
      },
      {
        kind: 'examples',
        items: [
          'I **have got to pay** my phone bill soon.',
          'She **has got to stop** doubting herself. **She’s gotta stop** doubting herself.',
        ],
      },
    ],
  },
  {
    slug: 'modals-of-possibility',
    title: 'Modals of Possibility & Prediction: Could, May and Might',
    blocks: [
      {
        kind: 'p',
        text: 'Modal verbs of possibility express the likelihood of a situation happening. They include can, may, might, and could.',
      },
      {
        kind: 'examples',
        items: [
          'Where is Charles? He **may be** in another city - he told us he had to solve a problem.',
          'Well, since he’s not here today, I **might see** him in our next meeting this week.',
          'Of course, he **could come** back by our next meeting.',
        ],
      },
      { kind: 'h3', text: 'How do we form sentences in the negative?' },
      {
        kind: 'p',
        text: 'To form the negative of these modals, we simply add NOT to the statement.',
      },
      {
        kind: 'examples',
        items: [
          'We **might not go** to the party tomorrow night - we had an exhausting week.',
          'Angela **may not like** your surprise party. She hates surprises.',
          'Your dreams **could not become** true if you don’t work for them to become true.',
        ],
      },
      { kind: 'h3', text: 'What is the difference?' },
      {
        kind: 'p',
        text: 'Basically none. Some people say that “may” is more probable than “might” and that “could” is more formal, but in the end it depends on the variety of English you are speaking.',
      },
      {
        kind: 'note',
        text: 'Remember to always consider context.',
      },
      {
        kind: 'examples',
        items: [
          'He **could finish** his project tonight. (possibility)',
          'He **could swim** when he was a child. (past ability)',
        ],
      },
    ],
  },
  {
    slug: 'future-continuous',
    title: 'The Future Continuous',
    blocks: [
      {
        kind: 'p',
        text: 'We use the future continuous to refer to temporary actions and events that will be in progress at a particular time in the future. It is formed using the construction will + be + the gerund of a verb (-ing form).',
      },
      {
        kind: 'examples',
        items: [
          'This time next week, **I’ll be taking** photographs with my new camera.',
          'Next month, they **will be flying** to Australia from Saudi Arabia.',
          'She **will not be working** on Tuesday, **she’ll be enjoying** the beach instead.',
          'Unfortunately we **won’t be attending** the wedding.',
          'What **will you be doing** by this time tomorrow?',
        ],
      },
      { kind: 'h3', text: 'Future Simple vs. Future Continuous' },
      {
        kind: 'p',
        text: 'The simple future tense is a verb tense that is used when an action is expected to occur in the future and be completed.',
      },
      {
        kind: 'p',
        text: 'For example, let’s suppose you have a meeting tomorrow at five o’clock. Then, you will say “I will arrive at five o’clock.” I will arrive is the simple future tense of the verb to arrive. You arrive once; beyond that, you can’t keep on arriving.',
      },
      {
        kind: 'p',
        text: 'However, once you get there, you may be doing something that goes on continuously, at least for a certain period of time. Then, you will say “At five o’clock, I will be meeting with the management about my raise.”',
      },
      {
        kind: 'p',
        text: 'Will be meeting is the future continuous tense of the verb to meet. The construction will + be + the present participle meeting indicates that the meeting isn’t going to happen in an instant, all at once. It will have a duration.',
      },
      {
        kind: 'note',
        text: 'The Future Continuous tense is used with action verbs only.',
      },
      {
        kind: 'examples',
        items: [
          'INCORRECT: I **will be being** stressed tomorrow during my science test.',
          'CORRECT: I **will be** stressed tomorrow during my science test.',
          'INCORRECT: When the sun comes out tomorrow, winter **will be seeming** like a distant memory.',
          'CORRECT: When the sun comes out tomorrow, winter **will seem** like a distant memory.',
          'INCORRECT: After I study, I **will be knowing** all the answers for the test.',
          'CORRECT: After I study, I **will know** all the answers for the test.',
        ],
      },
    ],
  },
  {
    slug: 'future-perfect',
    title: 'The Future Perfect',
    blocks: [
      {
        kind: 'p',
        text: 'The future perfect tense refers to a completed action in the future.',
      },
      {
        kind: 'p',
        text: 'When we use this tense we are projecting ourselves forward into the future and looking back at an action that will be completed some time later than now. It is most often used with a time expression.',
      },
      {
        kind: 'examples',
        items: [
          'I **will have been** here for six months on June 23rd.',
          'By the time you read this I **will have left**.',
          'You **will have finished** your report by this time next week.',
          '**Won\'t they have arrived** by 5:00?',
          '**Will you have eaten** when I pick you up?',
        ],
      },
      {
        kind: 'p',
        text: 'The future perfect is composed of two elements: WILL HAVE + Verb in the Participle Form',
      },
      { kind: 'h3', text: 'When Not to Use the Future Perfect' },
      {
        kind: 'p',
        text: 'The future perfect tense is only for actions that will be complete before a specified point in the future. In other words, the action you’re talking about must have a deadline. If you don’t mention a deadline, use the simple future tense instead of the future perfect tense.',
      },
      {
        kind: 'examples',
        items: [
          'Linda **will leave**. (Correct)',
          'Linda **will have left**. (Incorrect)',
        ],
      },
      {
        kind: 'p',
        text: 'The deadline can be very specific (eight o’clock) or it can be vague (next week). It can even depend on when something else happens (after the parade ends). It just has to be some time in the future.',
      },
      { kind: 'h3', text: 'Prepositional Phrases that Often Go With the Future Perfect' },
      {
        kind: 'examples',
        items: [
          '**By this time next week**, Linda **will have left** for her trip.',
          '**Three days from now**, we **will have finished** our project.',
          '**At midnight**, the party **will have ended**.',
          '**Will you have eaten** already?',
          'Chester **will not have arrived** **by the time** the parade is over.',
          '**When** I travel to France, I **will have been** to ten countries.',
          'My sister **will have cleaned** the bathroom **before the party**.',
          '**As soon as** someone buys this chair, I **will have sold** all the furniture I wanted to get rid of.',
        ],
      },
    ],
  },
  {
    slug: 'making-predictions',
    title: 'Making Predictions About the Future',
    blocks: [
      {
        kind: 'p',
        text: 'When we want to say what we think will happen in the future, we can either use will, followed by the verb without to, or going to followed by the verb.',
      },
      {
        kind: 'examples',
        items: [
          'What do you think **will happen** next year?',
          'Next week **is going to be** very busy, I think.',
          'There **won’t be** a rise in house prices next year.',
          'He **isn’t going to win** the election.',
        ],
      },
      { kind: 'h3', text: 'WILL vs. GOING TO' },
      {
        kind: 'p',
        text: 'If we are making an objective prediction based on something we see now, we should use going to and not will.',
      },
      {
        kind: 'examples',
        items: [
          'Watch out! **You’re going to hit** that car in front.',
          '**It’s going to be** a lovely day today – (there’s) not a cloud in the sky.',
        ],
      },
      { kind: 'h3', text: 'Other expressions used for predictions' },
      {
        kind: 'list',
        items: [
          'bound to = certain: “They **are bound to succeed**!”',
          'sure to = certain: “He **is sure to win** the championship.”',
          'likely to = probable: “We **are likely to win** the contract.”',
          'definite = sure: “He’s a **definite** good fit for the job!”',
          'probable: “**It’s probable that** we will be on holiday next week.”',
          'likely: “An election **is likely** next year.”',
          'will definitely happen: “There **will definitely be** a storm later.”',
          'will probably happen: “They **will probably take** on more staff.”',
          'may: “We **may be** able to help you.”',
          'might: “There **might be** a holiday next month – I’m not sure.”',
          'could: “There **could be** a bug in the system at any time now.”',
          '… is possible: “Do you think he will resign?” “Yes, **that’s possible**.”',
          '… is unlikely: “**It’s unlikely that** she will move.”',
          'will possibly: “**She’ll possibly tell** us tomorrow.”',
          'probably won’t: “They **probably won’t hear** until next week.”',
          'definitely won’t: “I **definitely won’t go** to the party.”',
          '… is highly unlikely: “**It’s highly unlikely that** the company will expand.”',
        ],
      },
    ],
  },
];
